import { Fragment, useEffect, useRef, useState } from "react";

import toast from "react-hot-toast";
import { Copy, Download, ExternalLink, Loader2, RotateCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { downloadFromUrl } from "@/lib/download-file";
import { cn } from "@/lib/utils";

import { CHANNEL_LABELS, ChannelIcon } from "./channel-icon";
import {
  BROADCAST_RETENTION_DAYS,
  PageMeta,
  Broadcast,
  BroadcastRun,
  RunDelivery,
  formatCronSchedule,
  isHttpUrl,
  broadcastsApi,
  runAt,
} from "./use-broadcasts";
import { artifactFileName } from "./download-captures";

const POLL_MS = 3000;
/** Runs per page; the engine caps a single page at 50. */
const PAGE_SIZE = 20;
const MAX_REFRESH = 50;

/** A run/delivery still in progress — keep polling until it settles. */
function runsActive(runs: BroadcastRun[]): boolean {
  return runs.some(
    (r) =>
      r.status === "pending" ||
      r.status === "capturing" ||
      (r.deliveries ?? []).some(
        (d) => d.status === "queued" || d.status === "pending",
      ),
  );
}

interface RunHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  broadcast: Broadcast | null;
}

/** "Today" / "Yesterday" / "Sep 3" (+ year when it differs) — the day-separator label. */
function runDayLabel(run: BroadcastRun): string {
  const d = new Date(runAt(run));
  if (Number.isNaN(d.getTime())) return "—";
  const now = new Date();
  const startOf = (x: Date): number =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOf(now) - startOf(d)) / 86_400_000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(d.getFullYear() !== now.getFullYear() ? { year: "numeric" as const } : {}),
  });
}

/** Time-only ("5:37 PM") — the date lives in the day separator above the row. */
function runTimeLabel(run: BroadcastRun): string {
  const raw = runAt(run);
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

/** Display label for one delivery — "#channel" for Slack, the channel type otherwise. */
function deliveryLabel(d: RunDelivery): string {
  return d.channelName ? `#${d.channelName}` : (CHANNEL_LABELS[d.channelType] ?? d.channelType);
}

/**
 * SCIO3's chip grammar: identical delivered/pending deliveries of a channel
 * collapse into ONE chip with a count ("Email ✓ ×2"); failed ones stay
 * individual so each keeps its own Retry.
 */
interface DeliveryChipGroup {
  key: string;
  channelType: string;
  label: string;
  bucket: "delivered" | "pending";
  count: number;
  /** Single delivered chip with a link deep-links (Slack permalink / PDF file). */
  url?: string;
}

function groupDeliveries(deliveries: RunDelivery[]): {
  groups: DeliveryChipGroup[];
  failed: RunDelivery[];
} {
  const groups = new Map<string, DeliveryChipGroup>();
  const failed: RunDelivery[] = [];
  for (const d of deliveries) {
    if (d.status === "failed") {
      failed.push(d);
      continue;
    }
    const bucket = d.status === "delivered" ? "delivered" : "pending";
    const label = deliveryLabel(d);
    const key = `${d.channelType}:${label}:${bucket}`;
    const existing = groups.get(key);
    if (existing) {
      existing.count += 1;
      existing.url = undefined; // ambiguous once grouped — the Files menu has them all
    } else {
      groups.set(key, {
        key,
        channelType: d.channelType,
        label,
        bucket,
        count: 1,
        url: isHttpUrl(d.providerMessageId) ? d.providerMessageId : undefined,
      });
    }
  }
  return { groups: [...groups.values()], failed };
}

/** Deliveries that carry an openable link (Slack permalink, PDF file). */
interface RunFile {
  label: string;
  url: string;
  /** A PDF delivery's URL is the file itself and downloads; a Slack one is a permalink and opens. */
  kind: "download" | "open";
}

function openableDeliveries(run: BroadcastRun): RunFile[] {
  const openable = (run.deliveries ?? []).filter((d) => isHttpUrl(d.providerMessageId));
  const counts = new Map<string, number>();
  return openable.map((d) => {
    const base = deliveryLabel(d);
    const n = (counts.get(base) ?? 0) + 1;
    counts.set(base, n);
    const dupes = openable.filter((o) => deliveryLabel(o) === base).length;
    return {
      label: dupes > 1 ? `${base} ${n}` : base,
      url: d.providerMessageId as string,
      kind: d.channelType === "pdf" ? "download" : "open",
    };
  });
}

/** Save a PDF delivery to disk; open anything else (a Slack permalink) in a tab. */
function openRunFile(file: RunFile, run: BroadcastRun, broadcastTitle?: string): void {
  if (file.kind === "download") {
    void downloadFromUrl(
      file.url,
      artifactFileName({
        broadcastName: broadcastTitle,
        date: runAt(run),
        url: file.url,
        fallbackExtension: "pdf",
      }),
    );
    return;
  }
  window.open(file.url, "_blank", "noopener,noreferrer");
}

/**
 * Per-broadcast run history — a centered dialog (SCIO3's RunHistoryDialog,
 * ported, then decluttered): day-separator rows (Today · Yesterday · Sep 3) with a
 * time-only When column, list-matching dot-and-word status, 2-line-clamped errors,
 * and quiet chips. One table row per run — When · Status · Deliveries · Files —
 * replacing the right-side drawer whose one-row-per-delivery cards let
 * six deliveries eat the whole panel. Data behavior is unchanged: first page +
 * live poll while a run is in flight, Load more, and per-delivery Retry.
 */
export function RunHistoryDialog({
  open,
  onOpenChange,
  broadcast,
}: RunHistoryDialogProps): JSX.Element {
  const [runs, setRuns] = useState<BroadcastRun[]>([]);
  const [meta, setMeta] = useState<PageMeta | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState<Set<string>>(new Set());
  // Bumped after a retry so the effect below reloads + resumes polling.
  const [pollEpoch, setPollEpoch] = useState(0);
  // How many rows are on screen — a refresh/poll refetches that many (capped at
  // the engine's 50/page) so Load-more results aren't clipped by a poll.
  const loadedRef = useRef(0);
  loadedRef.current = runs.length;

  // Load the first page, then poll every few seconds while any run is capturing
  // or a delivery is still queued — so "Capturing → Delivered" updates live.
  useEffect(() => {
    if (!open || !broadcast) return;

    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;
    const stop = (): void => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    const load = async (): Promise<BroadcastRun[]> => {
      const limit = Math.min(Math.max(loadedRef.current, PAGE_SIZE), MAX_REFRESH);
      const data = await broadcastsApi.getRuns(broadcast.id, { limit, offset: 0 });
      if (!cancelled) {
        setRuns(data.items);
        setMeta(data.meta);
      }
      return data.items;
    };

    if (pollEpoch === 0) setLoading(true);
    setError(null);
    load()
      .then((data) => {
        if (cancelled) return;
        setLoading(false);
        if (runsActive(data)) {
          timer = setInterval(() => {
            load()
              .then((d) => {
                if (!runsActive(d)) stop();
              })
              .catch(() => undefined);
          }, POLL_MS);
        }
      })
      .catch((err) => {
        console.error("Failed to load runs:", err);
        if (!cancelled) {
          setError("Failed to load run history");
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
      stop();
    };
  }, [open, broadcast, pollEpoch]);

  const loadMore = async (): Promise<void> => {
    if (!broadcast || loadingMore) return;
    setLoadingMore(true);
    try {
      const data = await broadcastsApi.getRuns(broadcast.id, {
        limit: PAGE_SIZE,
        offset: runs.length,
      });
      // Append, dropping anything already on screen (new runs shift offsets).
      const seen = new Set(runs.map((r) => r.id));
      setRuns([...runs, ...data.items.filter((r) => !seen.has(r.id))]);
      setMeta(data.meta);
    } catch (err) {
      console.error("Failed to load more runs:", err);
      toast.error("Failed to load more runs");
    } finally {
      setLoadingMore(false);
    }
  };

  const retryDelivery = async (runId: string, deliveryId: string): Promise<void> => {
    if (!broadcast || retrying.has(deliveryId)) return;
    setRetrying((prev) => new Set(prev).add(deliveryId));
    try {
      await broadcastsApi.retryDelivery(deliveryId);
      // Optimistic: show it queued right away; the poll tracks it to a verdict.
      setRuns((prev) =>
        prev.map((r) =>
          r.id !== runId
            ? r
            : {
                ...r,
                deliveries: (r.deliveries ?? []).map((d) =>
                  d.id === deliveryId
                    ? { ...d, status: "queued", error: null, providerMessageId: null }
                    : d,
                ),
              },
        ),
      );
      setPollEpoch((e) => e + 1);
      toast.success("Delivery retry queued");
    } catch (err) {
      console.error("Failed to retry delivery:", err);
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Failed to retry delivery";
      toast.error(message);
    } finally {
      setRetrying((prev) => {
        const next = new Set(prev);
        next.delete(deliveryId);
        return next;
      });
    }
  };

  const sendTo = [...new Set((broadcast?.channels ?? []).map((c) => c.type))]
    .map((t) => CHANNEL_LABELS[t] ?? t)
    .join(", ");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* testid keeps the drawer-era name — the e2e contract addresses this surface by it. */}
      {/* Wider than the usual dialog on purpose: a 5-column data table; narrower and the Error
          column collapses to a few characters per line. */}
      <DialogContent
        className="max-h-[85vh] overflow-y-auto sm:max-w-6xl"
        data-testid="runs-history-sheet"
      >
        <DialogHeader>
          <DialogTitle>Run history</DialogTitle>
          <DialogDescription className="truncate">
            {broadcast?.title}
            {sendTo && ` · ${sendTo}`}
            {broadcast && ` · ${formatCronSchedule(broadcast.cron, broadcast.timezone)}`}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <p className="py-8 text-center text-sm text-muted-foreground">{error}</p>
        ) : runs.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No runs yet. Use “Run now” to capture one now.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border">
            <Table className="min-w-[640px] table-fixed">
              <TableHeader>
                <TableRow className="bg-muted/40 hover:bg-muted/40">
                  <TableHead className="h-9 w-[96px] whitespace-nowrap text-[11px] font-semibold uppercase tracking-wide">
                    When
                  </TableHead>
                  <TableHead className="h-9 w-[150px] text-[11px] font-semibold uppercase tracking-wide">
                    Status
                  </TableHead>
                  <TableHead className="h-9 text-[11px] font-semibold uppercase tracking-wide">
                    Deliveries
                  </TableHead>
                  <TableHead className="h-9 min-w-[320px] text-[11px] font-semibold uppercase tracking-wide">
                    Error
                  </TableHead>
                  <TableHead className="h-9 w-[70px] text-[11px] font-semibold uppercase tracking-wide">
                    Files
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.map((run, i) => {
                  const label = runDayLabel(run);
                  const prev = i > 0 ? runDayLabel(runs[i - 1]) : null;
                  return (
                    <Fragment key={run.id}>
                      {label !== prev && (
                        <TableRow className="hover:bg-transparent">
                          <TableCell
                            colSpan={5}
                            className="bg-muted/40 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
                          >
                            {label}
                          </TableCell>
                        </TableRow>
                      )}
                      <RunRow
                        run={run}
                        broadcastTitle={broadcast?.title}
                        retrying={retrying}
                        onRetry={retryDelivery}
                      />
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {!loading && !error && meta?.hasMore && (
          <div className="flex flex-col items-center gap-1.5">
            <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
              Load more
            </Button>
            <span className="text-xs text-muted-foreground">
              Showing {runs.length} of {meta.total} runs
            </span>
          </div>
        )}

        <p className="border-t border-border pt-3 text-xs text-muted-foreground">
          Snapshots are stored for {BROADCAST_RETENTION_DAYS} days; older files are removed
          automatically.
        </p>
      </DialogContent>
    </Dialog>
  );
}

function RunRow({
  run,
  broadcastTitle,
  retrying,
  onRetry,
}: {
  run: BroadcastRun;
  /** Names a downloaded PDF, so it matches the same file saved from the Snapshots tab. */
  broadcastTitle?: string;
  retrying: Set<string>;
  onRetry: (runId: string, deliveryId: string) => Promise<void>;
}): JSX.Element {
  const inProgress =
    run.status === "pending" ||
    run.status === "capturing" ||
    (run.deliveries ?? []).some((d) => d.status === "queued" || d.status === "pending");
  const failed = run.status === "failed";
  const deliveries = run.deliveries ?? [];
  const { groups, failed: failedDeliveries } = groupDeliveries(deliveries);
  const files = openableDeliveries(run);
  // Every failure of this run, in full: the capture error, then one line per failed delivery.
  const errors: RunError[] = [
    ...(failed && run.error
      ? [{ key: "capture", label: "Capture", text: run.error, channelType: null }]
      : []),
    ...failedDeliveries
      .filter((d) => d.error)
      .map((d) => ({
        key: d.id,
        label: deliveryLabel(d),
        text: d.error as string,
        channelType: d.channelType,
      })),
  ];

  return (
    <TableRow data-testid="run-history-row">
      <TableCell className="py-3 align-top">
        <div className="whitespace-nowrap text-sm font-semibold tabular-nums text-foreground">
          {runTimeLabel(run)}
        </div>
        <div className="mt-0.5 text-[11px] text-muted-foreground">
          {run.manual ? "Manual" : "Scheduled"}
        </div>
      </TableCell>
      {/* Same dot-and-word grammar as the list's Last-run cell — one status language. */}
      <TableCell className="py-3 align-top">
        <span className="inline-flex items-center gap-1.5 text-sm">
          {inProgress ? (
            <Loader2 className="h-3 w-3 shrink-0 animate-spin text-primary" />
          ) : (
            <span
              className={cn(
                "h-1.5 w-1.5 shrink-0 rounded-full",
                failed ? "bg-red-500" : "bg-emerald-500",
              )}
            />
          )}
          <span
            className={cn(
              "font-medium capitalize",
              failed && "text-red-600 dark:text-red-400",
              !failed && !inProgress && "text-emerald-600 dark:text-emerald-400",
              inProgress && "text-muted-foreground",
            )}
          >
            {run.status === "pending" ? "queued" : run.status}
          </span>
          {!failed && run.captureMs ? (
            <span className="text-xs text-muted-foreground tabular-nums">
              · {Math.round(run.captureMs / 1000)}s
            </span>
          ) : null}
        </span>
        {failed && <div className="mt-0.5 text-[11px] text-muted-foreground">capture</div>}
      </TableCell>
      <TableCell className="py-3 align-top">
        {deliveries.length === 0 ? (
          <span className="text-sm text-muted-foreground">
            {failed ? "Not delivered" : "—"}
          </span>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {groups.map((g) => {
              const chip = (
                <span
                  key={g.key}
                  data-testid={`delivery-chip-${g.channelType}`}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs",
                    g.bucket === "delivered"
                      ? "border-emerald-500/40 text-emerald-700 dark:text-emerald-400"
                      : "border-border text-muted-foreground",
                  )}
                >
                  <ChannelIcon type={g.channelType} className="h-3 w-3" />
                  <span className="max-w-[140px] truncate">{g.label}</span>
                  {/* green already says "delivered" — only in-flight gets a glyph */}
                  {g.bucket === "pending" && "…"}
                  {g.count > 1 && <span className="opacity-70">×{g.count}</span>}
                </span>
              );
              return g.url ? (
                <a
                  key={g.key}
                  href={g.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-80"
                  title="Open"
                >
                  {chip}
                </a>
              ) : (
                chip
              );
            })}
            {failedDeliveries.map((d) => (
              <span
                key={d.id}
                data-testid={`delivery-chip-${d.channelType}`}
                title={d.error ?? undefined}
                className="inline-flex items-center gap-1.5 rounded-full border border-red-500/40 bg-red-50 px-2.5 py-0.5 text-xs text-red-700 dark:bg-red-500/10 dark:text-red-400"
              >
                <ChannelIcon type={d.channelType} className="h-3 w-3" />
                <span className="max-w-[140px] truncate">{deliveryLabel(d)}</span>✗
                <button
                  type="button"
                  data-testid={`retry-delivery-${d.id}`}
                  disabled={retrying.has(d.id)}
                  onClick={() => void onRetry(run.id, d.id)}
                  title="Retry this delivery (reuses the captured screenshot)"
                  className="font-semibold underline-offset-2 hover:underline disabled:opacity-50"
                >
                  <RotateCw
                    className={cn(
                      "mr-0.5 inline h-3 w-3",
                      retrying.has(d.id) && "animate-spin",
                    )}
                  />
                  Retry
                </button>
              </span>
            ))}
          </div>
        )}
      </TableCell>
      {/* Every failure of this run, in full — wrapped, copyable. The Status / Deliveries cells
          only say *that* it failed. */}
      <TableCell className="py-3 align-top" data-testid="run-history-error-cell">
        {errors.length === 0 ? (
          <span className="text-sm text-muted-foreground">—</span>
        ) : (
          <div className="flex flex-col gap-1.5">
            {errors.map((e) => (
              <RunErrorLine key={e.key} error={e} />
            ))}
          </div>
        )}
      </TableCell>
      <TableCell className="py-3 align-top">
        {inProgress && files.length === 0 ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : files.length === 0 ? (
          <span className="text-muted-foreground">—</span>
        ) : files.length === 1 ? (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-label={files[0].kind === "download" ? "Download file" : "Open file"}
            data-testid="run-history-file"
            onClick={() => openRunFile(files[0], run, broadcastTitle)}
          >
            {files[0].kind === "download" ? (
              <Download className="h-4 w-4" />
            ) : (
              <ExternalLink className="h-4 w-4" />
            )}
          </Button>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs">
                <ExternalLink className="h-3.5 w-3.5" />
                {files.length}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {files.map((f) => (
                <DropdownMenuItem key={f.url} onClick={() => openRunFile(f, run, broadcastTitle)}>
                  {f.kind === "download" ? (
                    <Download className="mr-2 h-3.5 w-3.5" />
                  ) : (
                    <ExternalLink className="mr-2 h-3.5 w-3.5" />
                  )}
                  {f.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </TableCell>
    </TableRow>
  );
}

interface RunError {
  key: string;
  label: string;
  text: string;
  channelType: string | null;
}

/** 404/405/410 from Teams or a webhook — the engine does not retry these; the URL must change. */
function isDeadDestination(text: string): boolean {
  return (
    /^Teams rejected the post \(HTTP 4\d\d\)/.test(text) ||
    /^Your webhook returned HTTP 4(04|05|10)\b/.test(text)
  );
}

/**
 * One failure, wrapped and copyable, inside the Error column — the Status/Deliveries cells only
 * say *that* it failed. Monospace at 11.5px matches the paused email's error box.
 */
function RunErrorLine({ error }: { error: RunError }): JSX.Element {
  const dead = isDeadDestination(error.text);
  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(error.text);
      toast.success("Copied");
    } catch {
      toast.error("Could not copy");
    }
  }
  return (
    <div className="flex items-start gap-2 text-[11.5px] leading-relaxed" data-testid="run-error-line">
      <div className="min-w-0 flex-1">
        <span className="mr-1.5 text-[10px] font-semibold uppercase tracking-wide text-red-600 dark:text-red-400">
          {error.label}
        </span>
        <span className="break-words font-mono text-foreground">{error.text}</span>
        {dead && (
          <div className="mt-0.5 font-sans text-xs text-muted-foreground">
            Not retried — fix the URL and run again.{" "}
            {error.channelType === "teams" && (
              <a
                href="https://links.optisigns.com/4hoQyTp"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                How to create a Teams webhook ↗
              </a>
            )}
          </div>
        )}
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="-mt-1 h-6 w-6 shrink-0"
        aria-label="Copy error"
        title="Copy"
        data-testid="run-error-copy"
        onClick={() => void copy()}
      >
        <Copy className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

